import {
    DeploymentBranding,
    ImageMimeType
} from 'phoenix/domain';
import {
    IUriPathService,
    AuthService,
    PhoenixToggleService
} from 'phoenix/services';
import { DeploymentBranding as ClientDeploymentBranding } from 'sharebase/domain/deployment-branding'

enum ThemeNames {
    'default',
    'custom'
}

enum PaletteNames {
    'sharebase',
    'custom'
}

interface MaterialColor {
    hex:string;
    value:number[];
    contrast:number[];
}

const primaryShareBaseBlue = '#00586E';
const yellowAccent = '#FFCA28';
const base64ShareBaseLogo = require('!!base64-image-loader!assets/logo@2x.png');
const lightTextContrast:[number, number, number, number] = [255, 255, 255, 1];
const darkTextContrast:[number, number, number, number] = [0, 0, 0, 0.87];

const defaultTheme = {
    name: ThemeNames[ThemeNames.default],
    branding: new ClientDeploymentBranding({
        EmailHeaderColor: primaryShareBaseBlue,
        ExternalShareLogoInBase64: base64ShareBaseLogo,
        ExternalShareLogoMimeType: 'image/png',
        IsActive: true,
        IsDefault: true,
        Links: null,
        LogoInBase64: base64ShareBaseLogo,
        LogoMimeType: 'image/png',
        SystemName: null
    })
} as Theme;

const defaultThemeHues = {
    primary: {
        'default': '500',
        'hue-1': '100',
        'hue-2': '200',
        'hue-3': 'A100'
    },
    accent: {
        'hue-1': '50',
        'hue-2': '100',
        'hue-3': 'A100'
    },
    warn: {
        'default': '500',
        'hue-1': '100',
        'hue-2': '700',
        'hue-3': 'A100'
    }
}

export interface Theme {
    name:string;
    branding:ClientDeploymentBranding;
}

/**
 * Service that exposes methods for retrieving and editing the email branding template.
 */
export class ThemingService {
    public static readonly $inject: string[] = [
        '$q',
        '$http',
        '$window',
        '$mdTheming',
        'pxUriPathService',
        'mdThemingProvider',
        'pxAuthService',
        'pxPhoenixToggleService'
    ];

    constructor(
        protected readonly $q:ng.IQService,
        private readonly $http:ng.IHttpService,
        private readonly $window:ng.IWindowService,
        private readonly $mdTheming,
        private readonly pxUriPathService:IUriPathService,
        private readonly mdThemingProvider,
        private readonly pxAuthService:AuthService,
        private readonly pxPhoenixToggleService:PhoenixToggleService
    ) {
        this.createDefaultTheme();
        this.mdThemingProvider.setDefaultTheme(defaultTheme.name);
        this.currentTheme = defaultTheme;
        this.activeWebLogo = defaultTheme.branding.webLogoInBase64;
    }

    public currentTheme:Theme;
    public activeWebLogo:string;
    public _usesCustomBranding = false;

    public applyBrandedTheme() {
        let self = this;
        let getBrandingPromise = this.$q.resolve();

        if(this.pxAuthService.isShareByLinkUser) {
            getBrandingPromise = this.getBranding()
                .then(branding => {
                    if (!branding) {
                        return;
                    }
                    this.setTheme({
                        name: ThemeNames[ThemeNames.custom],
                        branding: new ClientDeploymentBranding(branding)
                    } as Theme);
                    if (branding.IsActive) {
                        self._usesCustomBranding = true;
                        self.activeWebLogo = branding.ExternalShareLogoInBase64;
                    }
                });
        }
        
        return getBrandingPromise;
    }

    public setTheme(theme:Theme) {
        if(theme.branding != null && theme.branding.isActive  && !theme.branding.isDefault) {
            let brandedPalette = this.generatePalette(theme.branding.headerColor);

            this.mdThemingProvider.definePalette(PaletteNames[PaletteNames.custom], brandedPalette);
    
            this.mdThemingProvider.theme(theme.name)
                .primaryPalette(PaletteNames[PaletteNames.custom]);
                
            this.$mdTheming.generateTheme(theme.name);
        }
    }

    public get usesCustomBranding() {
        return this._usesCustomBranding;
    }

    public get toolbarThemeName() {
        return this.usesCustomBranding ? ThemeNames[ThemeNames.custom] : ThemeNames[ThemeNames.default];
    }

    /**
     * Massages the branding information so that it is in the expected format.
     * 
     * @param branding Branding object to massage
     */
    private buildApiBranding(branding: DeploymentBranding): DeploymentBranding {
        let data = angular.copy(branding);
        data.LogoInBase64 = this.buildDataUri(branding.LogoInBase64, branding.LogoMimeType);
        data.ExternalShareLogoInBase64 = this.buildDataUri(branding.ExternalShareLogoInBase64, branding.ExternalShareLogoMimeType);
        return data;
    }

    private buildDataUri(base64ImageString:string, imageType:ImageMimeType) {
        if(base64ImageString && imageType) {
            let meta = this.getImageMeta(imageType);
            return base64ImageString.indexOf(meta) >= 0 ? base64ImageString :
                `${meta}${base64ImageString}`;
        }
    }

    private createDefaultTheme() {
        let shareBasePalette = this.generatePalette(primaryShareBaseBlue);

        //color theme declarations
        //http://www.google.com/design/spec/style/color.html#color-color-palette
        this.mdThemingProvider.definePalette(PaletteNames[PaletteNames.sharebase], shareBasePalette);

        this.mdThemingProvider.theme(ThemeNames[ThemeNames.default])
            .primaryPalette(PaletteNames[PaletteNames.sharebase], defaultThemeHues.primary)
            .accentPalette('blue', defaultThemeHues.accent)
            .warnPalette('red', defaultThemeHues.warn);

        //generate the default theme that uses the primary palette named sharebase
        this.$mdTheming.generateTheme(ThemeNames[ThemeNames.default]);
    }

    private generatePalette(primaryHexColor:string) {
        let primaryMatColor = this.getPrimaryMatColor(primaryHexColor);
        let hue1MatColor = this.getHue1MatColor(primaryShareBaseBlue);
        let hue2MatColor = this.getHue2MatColor(primaryShareBaseBlue);
        let hue3MatColor = this.getHue3MatColor(primaryShareBaseBlue);
        let yellowAccentColor = this.getPrimaryMatColor(yellowAccent);
        let useLightTextContrast = primaryMatColor.contrast === lightTextContrast;
        let contrastColors = this.getContrastColors(useLightTextContrast, hue1MatColor, hue2MatColor, hue3MatColor);

        return {
            '50': primaryMatColor,
            '100': hue1MatColor, //#7fdcf2 for ShareBase
            '200': hue2MatColor, //#ebf2f3 for ShareBase
            '300': primaryMatColor,
            '400': primaryMatColor,
            '500': primaryMatColor,
            '600': primaryMatColor,
            '700': primaryMatColor,
            '800': primaryMatColor,
            '900': primaryMatColor,
            'A100': hue3MatColor, //55C9E9 for ShareBase
            'A200': hue3MatColor, //A200, A400, and A700 used to be #00cbee for ShareBase, but was not used
            'A400': yellowAccentColor,
            'A700': hue3MatColor,
            'contrastDefaultColor': useLightTextContrast ? 'light' : 'dark',
            'contrastLightColors': useLightTextContrast ? undefined : contrastColors,
            'contrastDarkColors': useLightTextContrast ? contrastColors : undefined
        };
    }

    private getBranding(): ng.IPromise<DeploymentBranding> {
        return this.$http.get<DeploymentBranding>(this.pxUriPathService.resolvePath('/{system}/branding'))
            .then(branding => this.isConfiguredBranding(branding.data) ? this.buildApiBranding(branding.data) : null);
    }

    private getContrast(hasDarkBackgroundColor:boolean) {
        return hasDarkBackgroundColor ? lightTextContrast : darkTextContrast;
    }

    private getContrastColors(isDefaultColorLight:boolean, hue1:MaterialColor, hue2:MaterialColor, hue3:MaterialColor) {
        let contrastColors = [];

        //If the default text contrast used is dark, then determine which hues use light contrast and add that to the array
        //or colors that require light text (do the opposite if the default text contrast is light)
        if(isDefaultColorLight) {
            if(hue1.contrast === darkTextContrast) {
                contrastColors[contrastColors.length] = '100';
            }
            if(hue2.contrast === darkTextContrast) {
                contrastColors[contrastColors.length] = '200';
            }
            if(hue3.contrast === darkTextContrast) {
                contrastColors[contrastColors.length] = 'A100';
            }
        }
        else {
            if(hue1.contrast === lightTextContrast) {
                contrastColors[contrastColors.length] = '100';
            }
            if(hue2.contrast === lightTextContrast) {
                contrastColors[contrastColors.length] = '200';
            }
            if(hue3.contrast === lightTextContrast) {
                contrastColors[contrastColors.length] = 'A100';
            }
        }

        return contrastColors;
    }

    private getHue1MatColor(primaryHexColor:string) {
        //Magic numbers came from finding out how to transform (via hsl) primary ShareBase theme color to the given hue1 color
        let hueHexValue = this.$window.tinycolor(primaryHexColor).spin(-1).desaturate(18).lighten(50).toHexString();
        let rgbValue = this.getRGBArray(hueHexValue);
        let contrastValue = this.getContrast(this.shouldUseLightText(hueHexValue));

        return {
            hex: hueHexValue,
            value: rgbValue,
            contrast: contrastValue
        } as MaterialColor;
    }

    private getHue2MatColor(primaryHexColor:string) {
        //Magic numbers came from finding out how to transform (via hsl) primary ShareBase theme color to the given hue2 color
        let hueHexValue = this.$window.tinycolor(primaryHexColor).spin(-4).desaturate(75).lighten(72).toHexString();
        let rgbValue = this.getRGBArray(hueHexValue);
        let contrastValue = this.getContrast(this.shouldUseLightText(hueHexValue));

        return {
            hex: hueHexValue,
            value: rgbValue,
            contrast: contrastValue
        } as MaterialColor;
    }

    private getHue3MatColor(primaryHexColor:string) {
        //Magic numbers came from finding out how to transform (via hsl) primary ShareBase theme color to the given hue3 color
        let hueHexValue = this.$window.tinycolor(primaryHexColor).spin(1).desaturate(23).lighten(40).toHexString();
        let rgbValue = this.getRGBArray(hueHexValue);
        let contrastValue = this.getContrast(this.shouldUseLightText(hueHexValue));

        return {
            hex: hueHexValue,
            value: rgbValue,
            contrast: contrastValue
        } as MaterialColor;
    }

    /**
     * Gets the metadata information for a base64 image data uri.
     * 
     * @param imageType The branding image type
     */
    private getImageMeta(imageType:ImageMimeType): string {
        return `data:${imageType};base64,`
    }

    private getPrimaryMatColor(primaryHexColor:string) {
        let rgbValue = this.getRGBArray(primaryHexColor);
        let contrastValue = this.getContrast(this.shouldUseLightText(primaryHexColor));

        return {
            hex: primaryHexColor,
            value: rgbValue,
            contrast: contrastValue
        } as MaterialColor;
    }

    private getRGBArray(hexValue:string) {
        let rgbObject = this.$window.tinycolor(hexValue).toRgb();
        return [+rgbObject.r, +rgbObject.g, +rgbObject.b];
    }

    /**
     * Check if a branding object was configured by the deployment or if it is the default branding.
     * 
     * @param branding The branding object to check
     * @returns True if it was configured by the deployment and false if it is the default branding.
     */
    private isConfiguredBranding(branding: DeploymentBranding): boolean {
        return !!(branding.LogoInBase64 && branding.EmailHeaderColor);
    }

    private shouldUseLightText(backgroundHexColor:string) {
        return this.$window.tinycolor.isReadable("#fff", backgroundHexColor);
    }
}



// WEBPACK FOOTER //
// ./src/sharebase/services/themingService.ts